import type { ReactNode } from 'react';
import { useTranslate } from '@refinedev/core';
import {
  Card,
  Col,
  Descriptions,
  Flex,
  Grid,
  Row,
  Space,
  Timeline,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import type { DescriptionsProps } from 'antd';
import type {
  PublicationListOut,
  SegmentDetailOut,
} from '@/api/generated/schemas';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { SecondsRange } from '@/components/common/SecondsRange';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { ProfileIconLink } from '@/components/account/ProfileIconLink';
import { PublicationInfoPopover } from '@/components/user_catalog/publications/PublicationInfoPopover';
import {
  ProductFormatTag,
  ProductVariantTag,
  SegmentSettlementTag,
} from '@/components/progress/tags';
import { formatDateTime, formatSecondsClock } from '@/utils/format';
import { PublicationDrawerIcon } from '@/components/user_catalog/publications/drawer/PublicationDrawerIcon';

type Props = {
  record: SegmentDetailOut;
  publication?: PublicationListOut;
};

export const SegmentGeneralTab = ({ record, publication }: Props) => {
  const t = useTranslate();
  const verticalDescProps = useVerticalDescProps();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  const basicItems: DescriptionsProps['items'] = [
    { key: 'id', label: t('base.fields.id'), children: orDash(record.id) },
    {
      key: 'segment_uuid',
      label: t('progress.segments.fields.segment_uuid'),
      children: orDash(
        record.segment_uuid && (
          <Typography.Text code>{record.segment_uuid}</Typography.Text>
        )
      ),
    },
    {
      key: 'publisher_id',
      label: t('progress.segments.fields.publisher_id'),
      children: orDash(record.publisher_id),
    },
    {
      key: 'account_id',
      label: t('progress.segments.fields.account_id'),
      children: orDash(record.account_id),
    },
    {
      key: 'profile_id',
      label: t('progress.segments.fields.profile_id'),
      children: orDash(
        record.profile_id != null && (
          <ProfileIconLink profileId={record.profile_id} />
        )
      ),
    },
    {
      key: 'device_id',
      label: t('progress.segments.fields.device_id'),
      children: orDash(
        record.device_id && (
          <Typography.Text code>{record.device_id}</Typography.Text>
        )
      ),
    },
    {
      key: 'session_id',
      label: t('progress.segments.fields.session_id'),
      children: orDash(
        record.session_id != null && (
          <ShowIconLink resource="progress.sessions" id={record.session_id} />
        )
      ),
    },
    {
      key: 'parent_cycle_id',
      label: t('progress.segments.fields.parent_cycle_id'),
      children: orDash(
        record.parent_cycle_id != null && (
          <Tooltip title={t('progress.segments.hints.parent_cycle_id')}>
            <span>{record.parent_cycle_id}</span>
          </Tooltip>
        )
      ),
    },
  ];

  const productItems: DescriptionsProps['items'] = [
    {
      key: 'publication_id',
      label: t('progress.segments.fields.publication_id'),
      children: orDash(
        record.publication_id != null && (
          <PublicationInfoPopover
            publication={publication}
            publicationId={record.publication_id}
          />
        )
      ),
    },
    {
      key: 'product_id',
      label: t('progress.segments.fields.product_id'),
      children: orDash(
        record.product_id != null && (
          <Space size={4}>
            {record.product_id}
            {record.publication_id != null && (
              <PublicationDrawerIcon publicationId={record.publication_id} />
            )}
          </Space>
        )
      ),
    },
    {
      key: 'product_version',
      label: t('progress.segments.fields.product_version'),
      children: orDash(record.product_version),
    },
    {
      key: 'product_kind',
      label: t('progress.segments.fields.product_kind'),
      children: orDash(
        record.product_kind && (
          <ProductFormatTag value={record.product_kind} labels="segments" />
        )
      ),
    },
    {
      key: 'product_variant',
      label: t('progress.segments.fields.product_variant'),
      children: orDash(
        record.product_variant && (
          <ProductVariantTag value={record.product_variant} />
        )
      ),
    },
    {
      key: 'product_duration',
      label: t('progress.segments.fields.product_duration'),
      children: orDash(
        record.product_duration != null && (
          <Tooltip title={`${record.product_duration} s`}>
            <span>
              {formatSecondsClock(record.product_duration, {
                alwaysHours: true,
              })}
            </span>
          </Tooltip>
        )
      ),
    },
    {
      key: 'product_demo_ratio',
      label: t('progress.segments.fields.product_demo_ratio'),
      children: orDash(record.product_demo_ratio),
    },
    {
      key: 'resource_href',
      label: t('progress.segments.fields.resource_href'),
      span: { xs: 1, sm: 2 },
      children: orDash(
        record.resource_href && (
          <Typography.Text
            code
            style={{ wordBreak: 'break-all', whiteSpace: 'normal' }}
          >
            {record.resource_href}
          </Typography.Text>
        )
      ),
    },
  ];

  const progressionItems: DescriptionsProps['items'] = [
    {
      key: 'total_start_progression',
      label: t('progress.segments.fields.total_start_progression'),
      children: orDash(record.total_start_progression?.toFixed(8)),
    },
    {
      key: 'total_end_progression',
      label: t('progress.segments.fields.total_end_progression'),
      children: orDash(record.total_end_progression?.toFixed(8)),
    },
    {
      key: 'fragment_range',
      label: t('progress.segments.fields.fragment_range'),
      span: { xs: 1, sm: 2 },
      children: orDash(
        record.fragment_range && (
          <SecondsRange
            value={record.fragment_range}
            hint={t('progress.segments.hints.fragment_range')}
          />
        )
      ),
    },
  ];

  // The segment's life in order; a missing stamp means the step is still ahead.
  const timelineSteps: {
    key: string;
    at: string | null;
    extra?: ReactNode;
  }[] = [
    { key: 'consumed_at', at: record.consumed_at },
    { key: 'synced_at', at: record.synced_at },
    { key: 'processed_at', at: record.processed_at },
    {
      key: 'accounted_at',
      at: record.accounted_at,
      extra: <SegmentSettlementTag value={record.settlement_status} />,
    },
  ];

  return (
    <Row gutter={[token.margin, token.margin]}>
      <Col flex="1 1 480px">
        <Flex vertical gap={token.margin}>
          <Card title={t('base.sections.basic_info')}>
            <Descriptions
              {...verticalDescProps}
              column={{ xs: 1, sm: 2 }}
              items={basicItems}
            />
          </Card>
          <Card title={t('progress.segments.sections.product')}>
            <Descriptions
              {...verticalDescProps}
              column={{ xs: 1, sm: 2 }}
              items={productItems}
            />
          </Card>
          <Card title={t('progress.segments.sections.progression')}>
            <Descriptions
              {...verticalDescProps}
              column={{ xs: 1, sm: 2 }}
              items={progressionItems}
            />
          </Card>
        </Flex>
      </Col>
      <Col flex={screens.lg ? '0 1 440px' : '0 1 300px'}>
        <Flex vertical gap={token.margin}>
          <Card title={t('progress.segments.sections.times')}>
            <Timeline
              items={timelineSteps.map(({ key, at, extra }) => ({
                key,
                color: at ? 'blue' : 'gray',
                children: (
                  <Flex vertical gap={token.marginXXS}>
                    <Typography.Text type={at ? undefined : 'secondary'}>
                      {t(`progress.segments.fields.${key}`)}
                    </Typography.Text>
                    <Typography.Text strong>
                      {orDash(formatDateTime(at))}
                    </Typography.Text>
                    {extra}
                  </Flex>
                ),
              }))}
            />
          </Card>
        </Flex>
      </Col>
    </Row>
  );
};
